import type { ar } from "@/lib/i18n/messages";
import type { SettlementMethod } from "@/lib/api/types";

type Messages = typeof ar;
type Selector = (m: Messages) => string;

/**
 * WHAT THE REFUND SHEET AND THE REFUND SLIP SAY ABOUT THE METHOD (R6, trap 1).
 *
 * A pure function, deliberately, and not a ternary inside the component. Two
 * things make it worth its own module:
 *
 * 1. **It is a money rule, so it has to be testable.** This app has no React
 *    test renderer, so a branch living in `RefundSheet.tsx` is a branch nothing
 *    can prove. The sentence beside the locked method is not decoration: it is
 *    what tells a cashier which drawer moves, and therefore what they will count
 *    at close. A wrong sentence here is a manager hunting a variance that is not
 *    there.
 * 2. **The sheet and the customer's slip must agree**, and both read this.
 *
 * THE SENTENCE NAMES THE DRAWER CONSEQUENCE, not just the method, because the
 * three refundable methods differ in exactly that (D26/D44):
 *
 * - `CASH` — notes leave this drawer; `expectedCash` falls.
 * - `CARD` — a credit on the terminal; the drawer is untouched and
 *   `expectedCard` falls.
 * - `INSTAPAY` — a transfer back out of the bank; NEITHER figure moves.
 *
 * The `CASH` copy is the fallback for anything else, and that is safe because
 * the sheet only ever opens on a method `isRefundOfferable` allowed — all three
 * of which are named above. It is a fallback rather than a `null` so a stale
 * bundle facing a newer server renders a sentence rather than a blank.
 */
export function refundMethodCopy(method: SettlementMethod | string | null): {
  label: Selector;
  sentence: Selector;
} {
  if (method === "CARD") {
    return {
      label: (m) => m.pos.card,
      sentence: (m) => m.pos.refundMethodLockedCard,
    };
  }
  if (method === "INSTAPAY") {
    return {
      label: (m) => m.pos.instapay,
      sentence: (m) => m.pos.refundMethodLockedInstapay,
    };
  }
  return {
    label: (m) => m.pos.cash,
    sentence: (m) => m.pos.refundMethodLockedCash,
  };
}
